import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Tipo de idioma soportado por la aplicación.
 * Si añadimos más idiomas, sólo hay que extender aquí, por ejemplo: 'en' | 'uk' | 'es'.
 */
export type AppLanguageCode = 'en' | 'uk';

/**
 * Modelo de una opción de idioma en la UI.
 */
export interface LanguageOption {
  /** Código del idioma (ej: 'en', 'uk') */
  code: AppLanguageCode;
  /** Clave de traducción para el label largo (menú del header) */
  labelKey: string;
  /** Etiqueta corta para chips/botones (ej: EN, UK, ES) */
  shortLabel: string;
}

const DEFAULT_LANGUAGE: AppLanguageCode = 'en';

@Injectable({ providedIn: 'root' })
export class AppLanguageService {
  private readonly translate = inject(TranslateService);

  /**
   * Lista centralizada de idiomas soportados.
   * Para añadir uno nuevo, basta con:
   * 1) Añadir el código en AppLanguageCode
   * 2) Añadir una entrada en este array
   * 3) Crear las claves de traducción correspondientes.
   */
  private readonly languages: LanguageOption[] = [
    { code: 'en', labelKey: 'app.header.language.en', shortLabel: 'EN' },
    { code: 'uk', labelKey: 'app.header.language.uk', shortLabel: 'UK' },
  ];

  /** Idioma actual como signal. Se lee con currentLanguage() */
  readonly currentLanguage = signal<AppLanguageCode>(DEFAULT_LANGUAGE);

  constructor() {
    // Registramos los idiomas disponibles en ngx-translate
    this.translate.addLangs(this.languages.map((l) => l.code));

    // Intentamos recuperar un idioma previo de localStorage
    const stored = localStorage.getItem('lang') as AppLanguageCode | null;
    const initial = this.isSupported(stored) ? stored! : DEFAULT_LANGUAGE;

    // setDefaultLang está deprecado → usamos setFallbackLang
    this.translate.setFallbackLang(DEFAULT_LANGUAGE);
    this.translate.use(initial);

    this.currentLanguage.set(initial);
  }

  /**
   * Devuelve la lista de idiomas que mostraremos en la UI.
   */
  getLanguages(): LanguageOption[] {
    return this.languages;
  }

  /**
   * Cambia el idioma de forma centralizada (login, header, etc. usan esto).
   */
  changeLanguage(lang: AppLanguageCode): void {
    if (!this.isSupported(lang)) {
      return;
    }

    if (this.currentLanguage() === lang) {
      return;
    }

    this.currentLanguage.set(lang);
    this.translate.use(lang);
    localStorage.setItem('lang', lang);
  }

  /**
   * Comprueba si el idioma está soportado.
   */
  private isSupported(lang: string | null): lang is AppLanguageCode {
    if (!lang) return false;
    return this.languages.some((l) => l.code === lang);
  }
}
